import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "./cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tinted"
  | "system"
  | "danger"
  | "dangerSoft"
  | "ghost"
  | "destructive";
export type ButtonSize = "md" | "sm" | "xs";

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active",
  secondary: "bg-sunken text-fg hover:bg-card-alt active:bg-card-alt",
  tinted: "bg-accent-subtle text-accent-fg hover:opacity-90 active:opacity-80",
  system: "bg-system-subtle text-system-fg hover:opacity-90 active:opacity-80",
  danger: "bg-error text-on-accent hover:opacity-90 active:opacity-80",
  dangerSoft: "bg-error-bg text-error hover:opacity-90 active:opacity-80",
  ghost: "bg-transparent text-accent-fg hover:bg-accent-subtle",
  // Always paired with an explicit confirmation.
  destructive: "bg-error text-on-accent hover:opacity-90 active:opacity-80",
};

const HIT_AREA =
  "after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:min-w-11 after:-translate-y-1/2 after:content-['']";

const SIZE: Record<ButtonSize, string> = {
  // Compact tinted pill (header actions); the hit area is grown back to 44px.
  xs: `h-9 min-w-9 px-3.5 text-footnote ${HIT_AREA}`,
  md: "h-(--control-h) min-h-(--control-min-h) min-w-11 px-5 text-body",
  // The visible mark is 40px, but the hit area is grown back to 44x44 by an
  // invisible pseudo-element: the design system's tap-target rule.
  sm: "h-10 min-w-11 px-4 text-footnote after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:min-w-11 after:-translate-y-1/2 after:content-['']",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Why the command cannot run right now. Renders the button disabled *and*
   * states the reason — the design system forbids a bare grey button.
   */
  unavailableReason?: string;
  loading?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  unavailableReason,
  loading = false,
  className,
  disabled,
  type = "button",
  children,
  ...props
}: ButtonProps) {
  const isDisabled =
    disabled === true || unavailableReason !== undefined || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        "relative inline-flex items-center justify-center gap-2 rounded-full font-sans font-semibold",
        "transition-[transform,background-color,opacity] motion-fast",
        "active:scale-[0.97] [@media(prefers-reduced-motion:reduce)]:active:scale-100",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        "disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-disabled disabled:active:scale-100",
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...props}
    >
      <span className={cn("contents", loading && "invisible")}>{children}</span>
      {loading && (
        <span
          aria-hidden="true"
          className="absolute size-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      )}
    </button>
  );
}

export interface IconButtonProps extends ButtonProps {
  /** Required: an icon-only control has no visible text to name it. */
  label: string;
}

export function IconButton({
  label,
  variant = "ghost",
  className,
  children,
  ...props
}: IconButtonProps) {
  return (
    <Button
      variant={variant}
      aria-label={label}
      className={cn("px-0", className)}
      {...props}
    >
      {children}
    </Button>
  );
}
